<!-- 选择会议室时间段 -->
<template>
    <div class="page">
        <van-nav-bar
            title="选择会议时间段"
            left-arrow
            fixed
            placeholder
            safe-area-inset-top
            @click-left="goBack"
        />

        <div class="content">
            <!-- 会议室封面 -->
            <div
                class="room-hero"
                :style="{ backgroundImage: `url(${room.cover})` }"
            >
                <div class="room-hero-mask"></div>
                <div class="room-hero-body">
                    <h1 class="room-name">{{ room.name }}<template v-if="room.floor">({{ room.floor }})</template></h1>
                    <p class="room-meta">
                        {{ room.location }} · {{ capacityText }} · 开放时间：{{ room.openTime }}
                    </p>
                </div>
            </div>

            <div class="panel">
                <!-- 选择日期 -->
                <div class="section">
                    <div class="section-head">
                        <span class="section-title">选择日期</span>
                        <span class="section-extra">{{ monthLabel }}</span>
                    </div>
                    <div class="date-list">
                        <button
                            v-for="(item, index) in dateOptions"
                            :key="item.key"
                            type="button"
                            class="date-item"
                            :class="{ active: selectedDateIndex === index }"
                            @click="selectDate(index)"
                        >
                            <span class="date-label">{{ item.label }}</span>
                            <span class="date-md">{{ item.md }}</span>
                            <span class="date-week">{{ item.week }}</span>
                        </button>
                    </div>
                </div>

                <!-- 选择时间段 -->
                <div class="section">
                    <div class="section-head">
                        <span class="section-title">选择会议时间段</span>
                        <span class="section-tip">每段1小时 · 可连续选择</span>
                    </div>
                    <div class="slot-grid">
                        <button
                            v-for="slot in timeSlots"
                            :key="slot.key"
                            type="button"
                            class="slot-item"
                            :class="{
                                active: isSlotSelected(slot.key),
                                disabled: (slot.booked && !slot.isMine) || slot.pastDue,
                                'booked-mine': slot.booked && slot.isMine,
                            }"
                            :disabled="slot.disabled"
                            @click="toggleSlot(slot)"
                        >
                            <span
                                v-if="slot.badgeText"
                                class="slot-badge"
                                :class="slot.badgeClass"
                            >{{ slot.badgeText }}</span>
                            <span class="slot-start">{{ slot.start }}</span>
                            <span class="slot-end">—{{ slot.end }}</span>
                        </button>
                    </div>
                </div>

                <!-- 已选摘要 -->
                <div class="selected-bar">
                    <span class="selected-text">
                        已选
                        <template v-if="selectedRangeText">
                            <em>{{ selectedRangeText }}</em>
                        </template>
                        <template v-else>
                            <em class="placeholder">请选择时间段</em>
                        </template>
                    </span>
                    <span class="selected-hours">{{ selectedHours }}小时</span>
                </div>

                <!-- 提示 -->
                <div class="notice">
                    <span class="notice-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" fill="#2f9b63"/>
                            <path d="M7.5 12.2l3 3 6-6.5" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </span>
                    <span class="notice-text">门禁权限将在预约开始前 5 分钟自动生效</span>
                </div>
            </div>
        </div>

        <!-- 底部操作栏 -->
        <div class="footer">
            <div class="fee-block">
                <div class="fee-label">费用合计</div>
                <div class="fee-value">¥{{ totalFee }}</div>
            </div>
            <button
                class="next-btn"
                type="button"
                :disabled="!selectedKeys.length"
                @click="goNext"
            >下一步</button>
        </div>
    </div>
</template>

<script>

import { getRoomInfoRequest } from '@/request/api'
import placeholderImage from '@/assets/icon_placeholderImage.png'
import { destroyKeepAliveCache } from '@/tools/keepAlive'
import { isUnauthorizedError } from '@/tools/utils'
import { Toast } from 'vant'

const WEEK_LABELS = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
const IMAGE_BASE = 'https://meetingroom.shdspf.cn'

function pad(n) {
    return n < 10 ? `0${n}` : `${n}`
}

function formatMD(date) {
    return `${date.getMonth() + 1}/${pad(date.getDate())}`
}

function formatKey(date) {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function normalizeTime(timeStr) {
    const raw = String(timeStr || '').trim()
    if (!raw) return ''
    const match = raw.match(/(\d{1,2}):(\d{2})/)
    if (!match) return raw
    return `${pad(Number(match[1]))}:${match[2]}`
}

function parseApiDate(dateStr) {
    const raw = String(dateStr || '').trim()
    if (!raw) return null
    const ymd = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)
    if (ymd) {
        const date = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
        if (!Number.isNaN(date.getTime())) return date
    }
    const date = new Date(raw)
    return Number.isNaN(date.getTime()) ? null : date
}

function dateRelativeLabel(date) {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const target = new Date(date)
    target.setHours(0, 0, 0, 0)
    const diff = Math.round((target - today) / 86400000)
    if (diff === 0) return '今天'
    if (diff === 1) return '明天'
    if (diff === 2) return '后天'
    return WEEK_LABELS[date.getDay()]
}

function resolveCoverUrl(url) {
    const raw = String(url || '').trim()
    if (!raw) return ''
    if (/^https?:\/\//i.test(raw)) return raw
    if (raw.startsWith('//')) return `https:${raw}`
    const path = raw.startsWith('/') ? raw : `/${raw}`
    return `${IMAGE_BASE}${path}`
}

function parseCapacity(tags) {
    if (!tags || !tags.length) return 8
    const hit = tags.find((t) => /可容纳\d+人/.test(t))
    if (!hit) return 8
    const num = hit.match(/(\d+)/)
    return num ? Number(num[1]) : 8
}

function isSameCalendarDay(a, b) {
    if (!a || !b) return false
    const d1 = new Date(a)
    const d2 = new Date(b)
    d1.setHours(0, 0, 0, 0)
    d2.setHours(0, 0, 0, 0)
    return d1.getTime() === d2.getTime()
}

/** 当天且当前时间已到达/超过时段开始时间（含进行中、已结束）则不可预约 */
function isSlotPastDueForToday(slot, selectedDate) {
    if (!selectedDate || !isSameCalendarDay(selectedDate, new Date())) return false
    const startPart = normalizeTime(slot.start)
    if (!startPart) return false
    const [h, m] = startPart.split(':').map(Number)
    if (Number.isNaN(h) || Number.isNaN(m)) return false
    const slotStart = new Date(selectedDate)
    slotStart.setHours(h, m, 0, 0)
    return Date.now() >= slotStart.getTime()
}

function buildSlots(openTime) {
    const [startStr, endStr] = (openTime || '09:00-18:00').split('-')
    const [sh, sm] = startStr.split(':').map(Number)
    const [eh] = endStr.split(':').map(Number)
    const slots = []
    let hour = sh
    let minute = sm || 0
    while (hour < eh) {
        const start = `${pad(hour)}:${pad(minute)}`
        const end = `${pad(hour)}:59`
        slots.push({
            key: `${start}-${end}`,
            start,
            end,
            startHour: hour,
            disabled: false,
        })
        hour += 1
    }
    return slots
}

export default {
    name: 'selectMeetingRoomTimeSlot',
    data() {
        return {
            room: {
                id: 0,
                name: '',
                floor: '',
                location: '',
                openTime: '09:00-18:00',
                tags: [],
                originPrice: 0,
                price: 0,
                cover: placeholderImage,
                smallCover: '',
                capacity: 0,
                adminPhone: '',
            },
            reservationInfos: [],
            dateOptions: [],
            selectedDateIndex: 0,
            selectedKeys: [],
            timeSlots: [],
            /** 与 query.id 同步，用于 keep-alive 下区分换会议室与从确认页返回 */
            _cachedRouteRoomId: null,
        }
    },
    computed: {
        capacityText() {
            return `${this.room.capacity || parseCapacity(this.room.tags)}人`
        },
        monthLabel() {
            const date = this.dateOptions[this.selectedDateIndex]
            if (!date) return ''
            return `${date.year}年${date.month}月`
        },
        selectedHours() {
            return this.selectedKeys.length
        },
        selectedRangeText() {
            if (!this.selectedKeys.length) return ''
            const sorted = this.sortedSelectedSlots()
            const first = sorted[0]
            const last = sorted[sorted.length - 1]
            return `${first.start}—${last.end}`
        },
        totalFee() {
            const price = Number(this.room.price) || 0
            return price * this.selectedHours
        },
    },
    activated() {
        this.initRoomId()
        const routeId = this.room.id
        if (!routeId) {
            this.$toast && this.$toast('缺少会议室信息')
            return
        }
        const roomChanged = routeId !== this._cachedRouteRoomId
        if (roomChanged) {
            this._cachedRouteRoomId = routeId
            this.selectedKeys = []
            this.selectedDateIndex = 0
            this.fetchRoomInfo({ resetSelection: true, showLoading: true })
        } else {
            this.fetchRoomInfo({ resetSelection: false, showLoading: false })
        }
    },
    beforeRouteLeave(to, from, next) {
        if (to.name === 'confirmBooking') {
            next()
            return
        }
        this.resetPageStateForLeave()
        next()
        this.$nextTick(() => {
            destroyKeepAliveCache(this)
        })
    },
    methods: {
        goBack() {
            this.$router.back()
        },
        resetPageStateForLeave() {
            this._cachedRouteRoomId = null
            this.selectedKeys = []
            this.selectedDateIndex = 0
            this.reservationInfos = []
            this.dateOptions = []
            this.timeSlots = []
        },
        initRoomId() {
            const q = this.$route.query || {}
            const id = Number(q.id)
            if (id) {
                this.room.id = id
            }
            const adminPhone = String(q.adminPhone || '').trim()
            if (adminPhone) {
                this.room.adminPhone = adminPhone
            }
        },
        applyRoomFromApi(data) {
            if (!data || typeof data !== 'object') return
            const buildingName = String(data.buildingName || '').trim()
            const cover = resolveCoverUrl(data.pic) || placeholderImage
            const smallCover = resolveCoverUrl(data.smallPic) || cover
            const apiPhone = String(data.adminPhone || '').trim()
            const floor = data.floor != null && String(data.floor).trim() !== ''
                ? String(data.floor).trim()
                : ''
            this.room = {
                ...this.room,
                id: data.id != null ? data.id : this.room.id,
                name: data.name || data.roomName || this.room.name || '未命名会议室',
                floor,
                location: buildingName || data.location || this.room.location || '—',
                openTime: data.openTime || data.openHours || this.room.openTime,
                cover,
                smallCover,
                capacity: data.capacity != null && data.capacity !== ''
                    ? Number(data.capacity)
                    : this.room.capacity,
                price: data.price != null ? Number(data.price) : this.room.price,
                originPrice: data.originPrice != null
                    ? Number(data.originPrice)
                    : (data.originalPrice != null ? Number(data.originalPrice) : this.room.originPrice),
                adminPhone: apiPhone || this.room.adminPhone || '',
            }
        },
        initDatesFromReservation(resetSelection = true) {
            const prevKey = !resetSelection && this.dateOptions[this.selectedDateIndex]
                ? this.dateOptions[this.selectedDateIndex].key
                : null
            const list = (this.reservationInfos || [])
                .map((item) => {
                    const date = parseApiDate(item.date)
                    if (!date) return null
                    date.setHours(0, 0, 0, 0)
                    return {
                        key: formatKey(date),
                        date,
                        year: date.getFullYear(),
                        month: date.getMonth() + 1,
                        label: dateRelativeLabel(date),
                        md: formatMD(date),
                        week: WEEK_LABELS[date.getDay()],
                        timeItems: Array.isArray(item.timeItems) ? item.timeItems : [],
                    }
                })
                .filter(Boolean)
            this.dateOptions = list
            if (resetSelection) {
                this.selectedDateIndex = 0
            } else if (prevKey) {
                const idx = list.findIndex((d) => d.key === prevKey)
                this.selectedDateIndex = idx >= 0
                    ? idx
                    : Math.min(this.selectedDateIndex, Math.max(0, list.length - 1))
            } else {
                this.selectedDateIndex = Math.min(
                    this.selectedDateIndex,
                    Math.max(0, list.length - 1),
                )
            }
        },
        getBookedInfoByStartForSelectedDate() {
            const selected = this.dateOptions[this.selectedDateIndex]
            const map = new Map()
            if (!selected) return map
            ;(selected.timeItems || []).forEach((item) => {
                const start = normalizeTime(item && item.time)
                if (!start) return
                map.set(start, {
                    isMine: item.isMine === true || item.isMine === 1 || item.isMine === 'true',
                    reservationStatus: item.reservationStatus,
                })
            })
            return map
        },
        refreshSlots() {
            const bookedMap = this.getBookedInfoByStartForSelectedDate()
            const selected = this.dateOptions[this.selectedDateIndex]
            const selectedDate = selected && selected.date
            const slots = buildSlots(this.room.openTime)
            slots.forEach((slot) => {
                const booked = bookedMap.get(normalizeTime(slot.start))
                if (booked) {
                    slot.booked = true
                    slot.isMine = booked.isMine
                    const status = Number(booked.reservationStatus)
                    if (status === 1) {
                        slot.badgeText = '待审核'
                        slot.badgeClass = 'slot-badge-pending'
                    } else if (booked.isMine) {
                        slot.badgeText = '我的预约'
                        slot.badgeClass = 'slot-badge-mine'
                    } else {
                        slot.badgeText = ''
                        slot.badgeClass = ''
                    }
                } else {
                    slot.booked = false
                    slot.isMine = false
                    slot.badgeText = ''
                    slot.badgeClass = ''
                }
                slot.pastDue = isSlotPastDueForToday(slot, selectedDate)
                slot.disabled = slot.booked || slot.pastDue
            })

            this.timeSlots = slots
            this.selectedKeys = this.selectedKeys.filter((key) => {
                const slot = slots.find((s) => s.key === key)
                return slot && !slot.disabled
            })
        },
        selectDate(index) {
            if (this.selectedDateIndex === index) return
            this.selectedDateIndex = index
            this.selectedKeys = []
            this.refreshSlots()
        },
        isSlotSelected(key) {
            return this.selectedKeys.includes(key)
        },
        sortedSelectedSlots() {
            return this.timeSlots.filter((s) => this.selectedKeys.includes(s.key))
        },
        toggleSlot(slot) {
            if (slot.disabled) return

            const keys = this.selectedKeys.slice()
            const idx = keys.indexOf(slot.key)

            if (idx > -1) {
                // 取消选中时，只允许从两端取消，保持连续
                const sorted = this.sortedSelectedSlots()
                const first = sorted[0]
                const last = sorted[sorted.length - 1]
                if (slot.key !== first.key && slot.key !== last.key) {
                    this.$toast && this.$toast('请保持时间段连续')
                    return
                }
                keys.splice(idx, 1)
                this.selectedKeys = keys
                return
            }

            if (!keys.length) {
                this.selectedKeys = [slot.key]
                return
            }

            const sorted = this.sortedSelectedSlots()
            const first = sorted[0]
            const last = sorted[sorted.length - 1]
            const all = this.timeSlots
            const firstIndex = all.findIndex((s) => s.key === first.key)
            const lastIndex = all.findIndex((s) => s.key === last.key)
            const curIndex = all.findIndex((s) => s.key === slot.key)

            const isAdjacent =
                curIndex === firstIndex - 1 || curIndex === lastIndex + 1

            if (!isAdjacent) {
                // 非相邻则重新从当前时段开始选
                this.selectedKeys = [slot.key]
                return
            }

            // 相邻扩展时，中间不能有已禁用时段
            const from = Math.min(curIndex, firstIndex)
            const to = Math.max(curIndex, lastIndex)
            const range = all.slice(from, to + 1)
            if (range.some((s) => s.disabled)) {
                this.$toast && this.$toast('所选时间段需连续且可预约')
                return
            }

            keys.push(slot.key)
            this.selectedKeys = keys
        },
        goNext() {
            if (!this.selectedKeys.length) {
                this.$toast && this.$toast('请选择会议时间段')
                return
            }
            const date = this.dateOptions[this.selectedDateIndex]
            this.$router.push({
                path: '/home/confirmBooking',
                query: {
                    id: this.room.id,
                    name: this.room.name,
                    floor: this.room.floor || '',
                    location: this.room.location,
                    openTime: this.room.openTime,
                    cover: this.room.smallCover || this.room.cover,
                    capacity: this.room.capacity,
                    price: this.room.price,
                    originPrice: this.room.originPrice,
                    adminPhone: this.room.adminPhone || '',
                    dateKey: date.key,
                    timeRange: this.selectedRangeText,
                    hours: this.selectedHours,
                },
            })
        },

        fetchRoomInfo(options = {}) {
            const { resetSelection = true, showLoading = true } = options
            if (!this.room.id) {
                this.$toast && this.$toast('缺少会议室信息')
                return
            }
            if (showLoading) {
                Toast.loading({
                    message: '正在加载',
                    forbidClick: true,
                    duration: 0,
                })
            }
            getRoomInfoRequest({
                id: this.room.id,
            })
                .then((res) => {
                    const data = (res && res.data != null) ? res.data : res
                    this.applyRoomFromApi(data)
                    const infos = data && data.reservationInfos
                    this.reservationInfos = Array.isArray(infos) ? infos : []
                    this.initDatesFromReservation(resetSelection)
                    if (resetSelection) {
                        this.selectedKeys = []
                    }
                    this.refreshSlots()
                })
                .catch((err) => {
                    if (isUnauthorizedError(err)) return
                    const msg = (err && (err.message || err.msg)) || '获取会议室信息失败'
                    this.$toast && this.$toast(msg)
                })
                .finally(() => {
                    if (showLoading) {
                        Toast.clear()
                    }
                })
        },
    },
}
</script>

<style scoped>
.page {
    min-height: 100vh;
    background: #eef3f9;
    box-sizing: border-box;
    padding-bottom: calc(1.36rem + env(safe-area-inset-bottom));
}

.content {
    padding-bottom: 0.24rem;
}

.room-hero {
    position: relative;
    height: 3.6rem;
    background-color: #8eadc8;
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
    overflow: hidden;
}

.room-hero-mask {
    position: absolute;
    inset: 0;
    background: linear-gradient(
        180deg,
        rgba(20, 40, 70, 0.1) 0%,
        rgba(15, 35, 60, 0.25) 45%,
        rgba(12, 28, 48, 0.72) 100%
    );
}

.room-hero-body {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    padding: 0.36rem 0.36rem 0.4rem;
    box-sizing: border-box;
}

.room-name {
    margin: 0;
    font-size: 0.4rem;
    font-weight: 700;
    color: #ffffff;
    line-height: 1.3;
}

.room-meta {
    margin: 0.1rem 0 0;
    font-size: 0.24rem;
    color: rgba(255, 255, 255, 0.88);
    line-height: 1.4;
}

.panel {
    position: relative;
    z-index: 2;
    margin-top: -0.28rem;
    padding: 0.36rem 0.32rem 0.2rem;
    background: #ffffff;
    border-radius: 0.32rem 0.32rem 0 0;
    box-sizing: border-box;
}

.section {
    margin-bottom: 0.4rem;
}

.section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.24rem;
}

.section-title {
    font-size: 0.3rem;
    font-weight: 700;
    color: #1e3a5f;
}

.section-extra,
.section-tip {
    font-size: 0.22rem;
    color: #9aa8bc;
}

.date-list {
    display: flex;
    gap: 0.16rem;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 0.04rem;
}

.date-list::-webkit-scrollbar {
    display: none;
}

.date-item {
    flex-shrink: 0;
    width: 1.48rem;
    padding: 0.18rem 0.08rem;
    border: 1px solid #e6edf5;
    border-radius: 0.2rem;
    background: #f7f9fc;
    color: #1e3a5f;
    cursor: pointer;
    box-sizing: border-box;
}

.date-item.active {
    border-color: #2b7de9;
    background: #2b7de9;
    color: #ffffff;
    box-shadow: 0 0.08rem 0.2rem rgba(43, 125, 233, 0.28);
}

.date-label,
.date-week {
    display: block;
    font-size: 0.2rem;
    line-height: 1.3;
    opacity: 0.85;
}

.date-md {
    display: block;
    margin: 0.08rem 0;
    font-size: 0.34rem;
    font-weight: 700;
    line-height: 1.2;
}

.slot-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.16rem;
}

.slot-item {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 1.12rem;
    padding: 0.16rem 0.08rem;
    border: 1px solid #e6edf5;
    border-radius: 0.2rem;
    background: #f7f9fc;
    color: #1e3a5f;
    cursor: pointer;
    box-sizing: border-box;
    overflow: hidden;
}

.slot-item.active {
    border-color: #2b7de9;
    background: #ffffff;
    color: #2b7de9;
    box-shadow: 0 0 0 0.02rem #2b7de9;
}

.slot-item.disabled {
    background: #f2f5f9;
    border-color: transparent;
    color: #c5ceda;
    cursor: not-allowed;
}

.slot-item.booked-mine {
    background: #e8f5ee;
    border-color: #b8dfc8;
    color: #2f9b63;
    cursor: not-allowed;
}

.slot-badge {
    position: absolute;
    top: 0;
    right: 0;
    padding: 0.02rem 0.1rem;
    border-radius: 0 0.2rem 0 0.12rem;
    color: #ffffff;
    font-size: 0.18rem;
    line-height: 1.35;
    white-space: nowrap;
}

.slot-badge-pending {
    background: #f0a020;
}

.slot-badge-mine {
    background: #2f9b63;
}

.slot-start {
    font-size: 0.28rem;
    font-weight: 600;
    line-height: 1.2;
}

.slot-end {
    margin-top: 0.04rem;
    font-size: 0.22rem;
    line-height: 1.2;
    opacity: 0.85;
}

.slot-item.disabled .slot-start,
.slot-item.disabled .slot-end {
    text-decoration: line-through;
}

.slot-item.booked-mine .slot-start,
.slot-item.booked-mine .slot-end {
    text-decoration: none;
}

.selected-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 0.96rem;
    padding: 0.2rem 0.28rem;
    margin-bottom: 0.24rem;
    border: 1px solid #d6e6fb;
    border-radius: 0.24rem;
    background: #f5f9ff;
    box-sizing: border-box;
}

.selected-text {
    font-size: 0.26rem;
    color: #5a6f8a;
}

.selected-text em {
    margin-left: 0.08rem;
    font-style: normal;
    font-weight: 700;
    color: #1e3a5f;
}

.selected-text .placeholder {
    font-weight: 400;
    color: #9aa8bc;
}

.selected-hours {
    font-size: 0.26rem;
    font-weight: 600;
    color: #2b7de9;
}

.notice {
    display: flex;
    align-items: center;
    padding: 0.2rem 0.24rem;
    margin-bottom: 0.16rem;
    border-radius: 0.2rem;
    background: #eef8f2;
    box-sizing: border-box;
}

.notice-icon {
    width: 0.36rem;
    height: 0.36rem;
    flex-shrink: 0;
    margin-right: 0.12rem;
}

.notice-icon svg {
    width: 100%;
    height: 100%;
    display: block;
}

.notice-text {
    font-size: 0.24rem;
    color: #2f9b63;
    line-height: 1.4;
}

.footer {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.24rem;
    padding: 0.2rem 0.32rem calc(0.2rem + env(safe-area-inset-bottom));
    background: #ffffff;
    box-shadow: 0 -0.04rem 0.2rem rgba(30, 58, 95, 0.06);
    box-sizing: border-box;
}

.fee-block {
    flex-shrink: 0;
}

.fee-label {
    font-size: 0.22rem;
    color: #8a9bb0;
    line-height: 1.3;
}

.fee-value {
    margin-top: 0.04rem;
    font-size: 0.4rem;
    font-weight: 700;
    color: #1e3a5f;
    line-height: 1.2;
}

.next-btn {
    flex: 1;
    height: 0.96rem;
    max-width: 4.4rem;
    border: none;
    border-radius: 0.48rem;
    background: #2b7de9;
    color: #ffffff;
    font-size: 0.32rem;
    font-weight: 600;
    cursor: pointer;
}

.next-btn:disabled {
    background: #a8c7ef;
    cursor: not-allowed;
}

.next-btn:not(:disabled):active {
    opacity: 0.9;
}

.page ::v-deep .van-nav-bar {
    background: #ffffff;
}

.page ::v-deep .van-nav-bar .van-nav-bar__title {
    color: #1e3a5f;
    font-weight: 600;
}

.page ::v-deep .van-nav-bar .van-icon {
    color: #2b7de9;
}
</style>
